import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const CONFIRM_PHRASE = "RESET DEMO PLACE DES ENERGIES";

export function ResetDemoModal({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1); // 1 = phrase, 2 = final
  const [input, setInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(1);
      setInput("");
      setSubmitting(false);
    }
  }, [open]);

  const phraseMatches = input === CONFIRM_PHRASE;

  const handleContinue = () => {
    if (phraseMatches) setStep(2);
  };

  const handleReset = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/reset-demo", {
        confirmation_phrase: CONFIRM_PHRASE,
      });
      const d = data?.deleted || {};
      toast.success(
        `Reset OK : ${d.leads || 0} leads, ${d.merchants || 0} commerçants, ${d.users || 0} utilisateurs, ${d.invitations || 0} invitations supprimés`,
        { duration: 8000 },
      );
      onOpenChange(false);
      if (onSuccess) onSuccess(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reset-demo-modal" className="max-w-lg">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center mb-2">
            <AlertTriangle className="w-6 h-6 text-[#B91C1C]" />
          </div>
          <DialogTitle className="font-display text-xl text-[#1F2937]">
            {step === 1 ? "Réinitialiser la plateforme ?" : "Dernière confirmation"}
          </DialogTitle>
          <DialogDescription className="text-[#4B5563] leading-relaxed pt-1">
            {step === 1
              ? "Toutes les données opérationnelles seront supprimées définitivement."
              : "Cette action est irréversible. Cliquez sur Réinitialiser maintenant pour exécuter."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <>
            <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#991B1B] mb-2">
                Sera supprimé
              </div>
              <ul className="space-y-1 text-[#7F1D1D]">
                <li>• Tous les leads / clients</li>
                <li>• Tous les commerçants</li>
                <li>• Tous les licenciés et collaborateurs</li>
                <li>• Tous les autres admins collaborateurs</li>
                <li>• Toutes les invitations (utilisées et en attente)</li>
                <li>• Tous les liens de reset password</li>
                <li>• L'historique d'audit (audit_logs)</li>
              </ul>
            </div>

            <div className="rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] px-4 py-3 text-sm">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#166534] mb-2">
                Sera conservé
              </div>
              <ul className="space-y-1 text-[#14532D]">
                <li>• Votre compte admin uniquement (mot de passe inchangé)</li>
              </ul>
            </div>

            <div className="space-y-1.5 pt-2">
              <Label htmlFor="reset-phrase" className="text-sm text-[#1F2937]">
                Pour confirmer, tapez exactement :{" "}
                <code className="font-mono text-[#B91C1C] font-semibold">{CONFIRM_PHRASE}</code>
              </Label>
              <Input
                id="reset-phrase"
                data-testid="reset-demo-phrase-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                autoComplete="off"
                placeholder={CONFIRM_PHRASE}
                className="font-mono"
              />
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="reset-demo-cancel"
                className="rounded-lg"
              >
                Annuler
              </Button>
              <Button
                type="button"
                onClick={handleContinue}
                disabled={!phraseMatches}
                data-testid="reset-demo-continue"
                className="rounded-lg bg-[#B91C1C] hover:bg-[#991B1B] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continuer
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 2 && (
          <>
            <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-6 text-center">
              <AlertTriangle className="w-10 h-10 text-[#B91C1C] mx-auto mb-3" />
              <p className="text-[#7F1D1D] font-semibold text-base mb-1">
                Action irréversible
              </p>
              <p className="text-[#991B1B] text-sm">
                La plateforme sera vidée à l'exception de votre compte admin.
              </p>
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                data-testid="reset-demo-cancel-final"
                className="rounded-lg"
              >
                Annuler
              </Button>
              <Button
                type="button"
                onClick={handleReset}
                disabled={submitting}
                data-testid="reset-demo-confirm-final"
                className="rounded-lg bg-[#B91C1C] hover:bg-[#991B1B] text-white font-semibold"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {submitting ? "Réinitialisation…" : "Réinitialiser maintenant"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ResetDemoModal;
